import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tab,
  Tabs,
  Paper,
  FormGroup,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Save as SaveIcon, 
  Add as AddIcon,
  Delete as DeleteIcon,
  List as ListIcon 
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { OpenAIConfig, AnonymizationConfig, CustomPatternCreate } from '../../types/api';
import AnonymizationLogs from './AnonymizationLogs';

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Panel component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
      style={{ padding: '16px 0' }}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface ConfigDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ConfigDrawer: React.FC<ConfigDrawerProps> = ({ open, onClose }) => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  // State for API key
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // State for OpenAI config
  const [model, setModel] = useState('gpt-4');
  const [temperature, setTemperature] = useState(0);
  const [maxTokens, setMaxTokens] = useState(150);

  // State for alerts
  const [alert, setAlert] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    show: boolean;
  }>({
    message: '',
    type: 'info',
    show: false,
  });
  
  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch current OpenAI config
  const { data: configData, isLoading: configLoading } = useQuery(
    'openAiConfig',
    api.config.getOpenAiConfig,
    {
      onSuccess: (data) => {
        setModel(data.model);
        setTemperature(data.temperature);
        setMaxTokens(data.max_tokens);
      },
      onError: () => {
        setAlert({
          message: 'Failed to load configuration.',
          type: 'error',
          show: true,
        });
      },
    }
  );

  // Mutation for updating API key
  const updateApiKeyMutation = useMutation(api.config.updateApiKey, {
    onSuccess: () => {
      setAlert({
        message: 'API key updated successfully.',
        type: 'success',
        show: true,
      });
      // Reset the form field for security
      setApiKey('');
      // Invalidate queries that might depend on the API key
      queryClient.invalidateQueries('openAiConfig');
    },
    onError: (error) => {
      setAlert({
        message: `Failed to update API key: ${error}`,
        type: 'error',
        show: true,
      });
    },
  });

  // Mutation for updating OpenAI config
  const updateConfigMutation = useMutation(api.config.updateOpenAiConfig, {
    onSuccess: () => {
      setAlert({
        message: 'Configuration updated successfully.',
        type: 'success',
        show: true,
      });
      queryClient.invalidateQueries('openAiConfig');
    },
    onError: (error) => {
      setAlert({
        message: `Failed to update configuration: ${error}`,
        type: 'error',
        show: true,
      });
    },
  });

  // Function to update API key
  const handleUpdateApiKey = () => {
    if (apiKey) {
      updateApiKeyMutation.mutate(apiKey);
    }
  };

  // Function to update OpenAI config
  const handleUpdateConfig = () => {
    const newConfig: OpenAIConfig = {
      model,
      temperature,
      max_tokens: maxTokens,
    };
    updateConfigMutation.mutate(newConfig);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: fullScreen ? '100%' : 600,
          p: 3,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Settings</Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {alert.show && (
        <Alert
          severity={alert.type}
          sx={{ mb: 3 }}
          onClose={() => setAlert({ ...alert, show: false })}
        >
          {alert.message}
        </Alert>
      )}
      
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="configuration tabs"
        variant="fullWidth"
        sx={{ mb: 2 }}
      >
        <Tab label="OpenAI API" id="tab-0" aria-controls="tabpanel-0" />
        <Tab label="Anonymization" id="tab-1" aria-controls="tabpanel-1" />
        <Tab label="Logs" id="tab-2" aria-controls="tabpanel-2" icon={<ListIcon />} iconPosition="start" />
      </Tabs>
      
      <TabPanel value={tabValue} index={0}>
        <Typography variant="h6" gutterBottom>
          OpenAI API Configuration
        </Typography>

      {/* API Key Section */}
      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        API Key
      </Typography>
      <TextField
        fullWidth
        type={showApiKey ? 'text' : 'password'}
        variant="outlined"
        placeholder="Enter your OpenAI API key"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        margin="normal"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={() => setShowApiKey(!showApiKey)}
                sx={{ mr: 1 }}
              >
                {showApiKey ? 'Hide' : 'Show'}
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={!apiKey || updateApiKeyMutation.isLoading}
                onClick={handleUpdateApiKey}
                startIcon={
                  updateApiKeyMutation.isLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                Save
              </Button>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Model Selection */}
      <Typography variant="subtitle2" gutterBottom>
        Model
      </Typography>
      <FormControl fullWidth margin="normal">
        <Select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          variant="outlined"
          size="small"
        >
          <MenuItem value="gpt-4">GPT-4</MenuItem>
          <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
        </Select>
      </FormControl>

      {/* Temperature Slider */}
      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        Temperature: {temperature.toFixed(1)}
      </Typography>
      <Box sx={{ px: 1 }}>
        <Slider
          value={temperature}
          onChange={(_, newValue) => setTemperature(newValue as number)}
          step={0.1}
          marks
          min={0}
          max={1}
          valueLabelDisplay="auto"
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        Lower values produce more deterministic results, higher values more creative ones.
      </Typography>

      {/* Max Tokens Slider */}
      <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
        Max Tokens: {maxTokens}
      </Typography>
      <Box sx={{ px: 1 }}>
        <Slider
          value={maxTokens}
          onChange={(_, newValue) => setMaxTokens(newValue as number)}
          step={10}
          marks
          min={50}
          max={500}
          valueLabelDisplay="auto"
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        Maximum number of tokens to generate. More tokens may result in more comprehensive SQL
        queries.
      </Typography>

        {/* Save Button for OpenAI Config */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateConfig}
            disabled={configLoading || updateConfigMutation.isLoading}
            startIcon={
              updateConfigMutation.isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
          >
            {updateConfigMutation.isLoading ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Box>
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <AnonymizationSettings />
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <AnonymizationLogs />
      </TabPanel>
    </Drawer>
  );
};

// Anonymization Settings Component
const AnonymizationSettings: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  
  // States for anonymization config
  const [enabled, setEnabled] = useState(true);
  const [sensitivityLevel, setSensitivityLevel] = useState(2);
  const [customPatterns, setCustomPatterns] = useState<Record<string, string>>({});
  const [enabledPiiTypes, setEnabledPiiTypes] = useState<string[]>([]);
  const [debug, setDebug] = useState(false);
  const [logAnonymizedTexts, setLogAnonymizedTexts] = useState(false);
  const [logDetectedEntities, setLogDetectedEntities] = useState(false);
  
  // States for custom pattern dialog
  const [patternDialogOpen, setPatternDialogOpen] = useState(false);
  const [newPatternName, setNewPatternName] = useState('');
  const [newPatternRegex, setNewPatternRegex] = useState('');
  const [patternError, setPatternError] = useState('');
  
  // All available PII types
  const availablePiiTypes = [
    { value: 'date', label: 'Dates' },
    { value: 'name', label: 'Personal Names' },
    { value: 'location', label: 'Group/Client Names' }
  ];
  
  // Fetch current anonymization config
  const { data: configData, isLoading: configLoading } = useQuery(
    'anonymizationConfig',
    api.config.getAnonymizationConfig,
    {
      onSuccess: (data) => {
        setEnabled(data.enabled);
        setSensitivityLevel(data.sensitivity_level);
        setCustomPatterns(data.custom_patterns);
        setEnabledPiiTypes(data.enabled_pii_types);
        setDebug(data.logging.debug);
        setLogAnonymizedTexts(data.logging.log_anonymized_texts);
        setLogDetectedEntities(data.logging.log_detected_entities);
      },
      onError: (error) => {
        console.error('Failed to load anonymization configuration', error);
      },
    }
  );
  
  // Mutation for updating anonymization config
  const updateConfigMutation = useMutation(api.config.updateAnonymizationConfig, {
    onSuccess: () => {
      queryClient.invalidateQueries('anonymizationConfig');
    },
    onError: (error) => {
      console.error('Failed to update anonymization configuration', error);
    },
  });
  
  // Mutation for adding custom pattern
  const addPatternMutation = useMutation(api.config.addCustomPattern, {
    onSuccess: () => {
      queryClient.invalidateQueries('anonymizationConfig');
      setPatternDialogOpen(false);
      resetPatternForm();
    },
    onError: (error) => {
      setPatternError(`Failed to add pattern: ${error}`);
    },
  });
  
  // Mutation for removing custom pattern
  const removePatternMutation = useMutation(api.config.removeCustomPattern, {
    onSuccess: () => {
      queryClient.invalidateQueries('anonymizationConfig');
    },
    onError: (error) => {
      console.error('Failed to remove pattern', error);
    },
  });
  
  // Mutation for toggling PII type
  const togglePiiTypeMutation = useMutation(
    ({ piiType, enable }: { piiType: string; enable: boolean }) => 
      enable ? api.config.enablePIIType(piiType) : api.config.disablePIIType(piiType),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('anonymizationConfig');
      },
      onError: (error) => {
        console.error('Failed to toggle PII type', error);
      },
    }
  );
  
  // Handle toggle of enabled state
  const handleToggleEnabled = () => {
    setEnabled(!enabled);
    api.config.toggleAnonymization(!enabled)
      .then(() => {
        queryClient.invalidateQueries('anonymizationConfig');
      })
      .catch(error => {
        console.error('Failed to toggle anonymization', error);
      });
  };
  
  // Handle changes to sensitivity level
  const handleSensitivityChange = (event: SelectChangeEvent<number>) => {
    setSensitivityLevel(Number(event.target.value));
  };
  
  // Handle PII type toggle
  const handlePiiTypeToggle = (piiType: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    // Use optimistic UI update
    if (checked) {
      setEnabledPiiTypes([...enabledPiiTypes, piiType]);
    } else {
      setEnabledPiiTypes(enabledPiiTypes.filter(type => type !== piiType));
    }
    
    togglePiiTypeMutation.mutate({ piiType, enable: checked });
  };
  
  // Open custom pattern dialog
  const handleOpenPatternDialog = () => {
    setPatternDialogOpen(true);
  };
  
  // Close custom pattern dialog
  const handleClosePatternDialog = () => {
    setPatternDialogOpen(false);
    resetPatternForm();
  };
  
  // Reset pattern form
  const resetPatternForm = () => {
    setNewPatternName('');
    setNewPatternRegex('');
    setPatternError('');
  };
  
  // Add new custom pattern
  const handleAddPattern = () => {
    if (!newPatternName || !newPatternRegex) {
      setPatternError('Both name and pattern are required');
      return;
    }
    
    // Check if pattern name already exists
    if (customPatterns[newPatternName]) {
      setPatternError('Pattern with this name already exists');
      return;
    }
    
    // Try to validate the regex pattern
    try {
      new RegExp(newPatternRegex);
    } catch (error) {
      setPatternError('Invalid regular expression pattern');
      return;
    }
    
    const pattern: CustomPatternCreate = {
      name: newPatternName,
      pattern: newPatternRegex
    };
    
    addPatternMutation.mutate(pattern);
  };
  
  // Remove custom pattern
  const handleRemovePattern = (name: string) => {
    removePatternMutation.mutate(name);
  };
  
  // Save all anonymization configuration
  const handleSaveConfig = () => {
    const config: AnonymizationConfig = {
      enabled,
      sensitivity_level: sensitivityLevel,
      custom_patterns: customPatterns,
      enabled_pii_types: enabledPiiTypes,
      logging: {
        debug,
        log_anonymized_texts: logAnonymizedTexts,
        log_detected_entities: logDetectedEntities
      }
    };
    
    updateConfigMutation.mutate(config);
  };
  
  // Map sensitivity level to display text
  const getSensitivityLabel = (level: number) => {
    switch (level) {
      case 1: return 'Low';
      case 2: return 'Medium';
      case 3: return 'High';
      default: return 'Medium';
    }
  };
  
  if (configLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Anonymization Configuration
      </Typography>
      
      {/* Main toggle */}
      <Box sx={{ mb: 4 }}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggleEnabled}
              color="primary"
            />
          }
          label="Enable anonymization"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          When enabled, sensitive data in OpenAI requests will be anonymized
        </Typography>
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Sensitivity Level */}
      <Typography variant="subtitle1" gutterBottom>
        Sensitivity Level: {getSensitivityLabel(sensitivityLevel)}
      </Typography>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <Select
          value={sensitivityLevel}
          onChange={handleSensitivityChange}
          disabled={!enabled}
        >
          <MenuItem value={1}>Low (Fewer false positives, may miss some PII)</MenuItem>
          <MenuItem value={2}>Medium (Balanced sensitivity)</MenuItem>
          <MenuItem value={3}>High (Catch most PII, may have more false positives)</MenuItem>
        </Select>
        <Typography variant="caption" color="text.secondary">
          Higher sensitivity may detect more PII but could include false positives
        </Typography>
      </FormControl>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Custom Patterns */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">
            Custom Patterns
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={handleOpenPatternDialog}
            disabled={!enabled}
          >
            Add Pattern
          </Button>
        </Box>
        
        {Object.keys(customPatterns).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No custom patterns defined
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: '200px', overflow: 'auto' }}>
            {Object.entries(customPatterns).map(([name, pattern]) => (
              <ListItem
                key={name}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemovePattern(name)}
                    disabled={!enabled}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={name}
                  secondary={pattern}
                />
              </ListItem>
            ))}
          </List>
        )}
        <Typography variant="caption" color="text.secondary">
          Define custom regex patterns for organization-specific sensitive data
        </Typography>
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      {/* PII Types */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          PII Types to Detect
        </Typography>
        <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 2 }}>
          {availablePiiTypes.map((type) => (
            <FormControlLabel
              key={type.value}
              control={
                <Checkbox
                  checked={enabledPiiTypes.includes(type.value)}
                  onChange={handlePiiTypeToggle(type.value)}
                  disabled={!enabled}
                />
              }
              label={type.label}
            />
          ))}
        </FormGroup>
        <Typography variant="caption" color="text.secondary">
          Select the types of PII that should be detected and anonymized
        </Typography>
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Logging Options */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Logging Options
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={debug}
                onChange={(e) => setDebug(e.target.checked)}
                disabled={!enabled}
              />
            }
            label="Enable debug logging"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={logAnonymizedTexts}
                onChange={(e) => setLogAnonymizedTexts(e.target.checked)}
                disabled={!enabled}
              />
            }
            label="Log anonymized texts"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={logDetectedEntities}
                onChange={(e) => setLogDetectedEntities(e.target.checked)}
                disabled={!enabled}
              />
            }
            label="Log detected entities"
          />
        </FormGroup>
        <Typography variant="caption" color="text.secondary">
          These options are useful for debugging anonymization issues
        </Typography>
      </Box>
      
      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveConfig}
          disabled={!enabled || updateConfigMutation.isLoading}
          startIcon={
            updateConfigMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          {updateConfigMutation.isLoading ? 'Saving...' : 'Save Anonymization Settings'}
        </Button>
      </Box>
      
      {/* Custom Pattern Dialog */}
      <Dialog open={patternDialogOpen} onClose={handleClosePatternDialog}>
        <DialogTitle>Add Custom Pattern</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Define a custom regex pattern to detect organization-specific sensitive data.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Pattern Name"
            fullWidth
            variant="outlined"
            value={newPatternName}
            onChange={(e) => setNewPatternName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Regex Pattern"
            fullWidth
            variant="outlined"
            value={newPatternRegex}
            onChange={(e) => setNewPatternRegex(e.target.value)}
            placeholder="e.g. \bEMP-\d{6}\b"
            helperText="Valid JavaScript regular expression pattern"
          />
          {patternError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {patternError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePatternDialog}>Cancel</Button>
          <Button 
            onClick={handleAddPattern}
            variant="contained"
            disabled={!newPatternName || !newPatternRegex}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigDrawer;
